import { Box, Modal } from '@mui/material';
import ActionsMenu from 'apps/studio/studio-tab/components/actions-menu/ActionsMenu';
import { changeAnythingModalStyles } from 'apps/studio/studio-tab/components/change-anything-feed-modal/ChangeAnythingModalStyles';
import * as loadingAnimationData from 'core/assets/lottie/green-loader-animation.json';
import { ReactComponent as Cross } from 'core/assets/svg/common/cross.svg';
import { ReactComponent as Delete } from 'core/assets/svg/export-image/delete.svg';
import { ReactComponent as DownloadDark } from 'core/assets/svg/export-image/download-dark.svg';
import { ReactComponent as DownloadLoading } from 'core/assets/svg/export-image/download-loading.svg';
import BaseButton from 'core/components/button/BaseButton';
import BaseImage from 'core/components/image/BaseImage';
import { LayoutOrientation } from 'core/constants/enums/LayoutOrientation.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import LottieAnimation from 'core/layouts/components/lottie-animation/LottieAnimation';
import { reusableStyles } from 'core/layouts/ui-blocks/ReusableStyles';
import { HISTORY } from 'core/navigation/Routes';
import {
	feedPromptModalDetailsSelector,
	feedPromptsModalTypeSelector,
	openFeedChangeAnythingModalSelector,
	toggleFeedChangeAnythingModal,
} from 'core/store/reducers/appReducer/AppSlice';
import { toggleMultiDeletePromptConfirmationModal } from 'core/store/reducers/studioReducer/StudioSlice';
import { downloadImage } from 'core/utils/utils';
import * as React from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

function ChangeAnythingFeedModal() {
	const dispatch = useDispatch();

	const isModalOpen = useSelector(openFeedChangeAnythingModalSelector);
	const selectedPrompt = useSelector(feedPromptModalDetailsSelector);
	const feedPromptsModalType = useSelector(feedPromptsModalTypeSelector);
	const open = isModalOpen && feedPromptsModalType === HISTORY;
	const handleClose = () => {
		dispatch(toggleFeedChangeAnythingModal({ openModal: false }));
	};

	const disableButtons = useMemo(() => selectedPrompt.loading, [selectedPrompt.loading]);

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="prompt-info-modal"
			aria-describedby="prompt details"
			sx={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2.5px)' }}
		>
			<Box id="modal-wrapper" sx={changeAnythingModalStyles.modalWrapper} onClick={handleClose}>
				<Box id="modal-body" sx={changeAnythingModalStyles.modalBody}>
					<BaseButton
						id="cross-button"
						onClick={(e) => {
							e.stopPropagation();
							handleClose();
						}}
						styles={[changeAnythingModalStyles.crossButton]}
					>
						<Cross id="cross-icon" />
					</BaseButton>

					<Box
						id="content-wrapper"
						sx={[changeAnythingModalStyles.contentWrapper]}
						onClick={(e) => e.stopPropagation()}
					>
						<Box id="detail-screen-content" sx={[changeAnythingModalStyles.detailScreenContent]}>
							<Box id="image-wrapper" sx={changeAnythingModalStyles.detailScreenImgWrapper}>
								<Box id="image-cont" sx={changeAnythingModalStyles.detailScreenImgCont}>
									<Box id="image" sx={changeAnythingModalStyles.image}>
										{selectedPrompt.loading ? (
											<Box sx={{ height: '90px', width: '180px' }}>
												<LottieAnimation animationData={loadingAnimationData} loop />
											</Box>
										) : (
											<BaseImage src={selectedPrompt?.thumbnail} alt="" />
										)}
									</Box>
								</Box>
							</Box>

							<Box id="details-cont" sx={changeAnythingModalStyles.promptDetailsCont}>
								<Box
									id="details-scroll-wrapper"
									sx={changeAnythingModalStyles.promptDetailsScrollWrapper}
								>
									<Box id="prompt-details" sx={reusableStyles.flexCol}>
										<Box id={ReusedComponentIDs.PROMPT_MODAL_SCROLL_IDHAR} />
										<ActionsMenu
											image={selectedPrompt?.media}
											handleClose={handleClose}
											orientation={LayoutOrientation.HORIZONTAL}
											source={feedPromptsModalType}
											imageId={selectedPrompt?.generationId}
										/>
									</Box>
								</Box>
								<Box
									id="download-delete-cont-wrapper"
									sx={changeAnythingModalStyles.detailScreenButtonsCont}
								>
									<Box sx={[reusableStyles.flexRow, { flexGrow: 1, gap: 'inherit' }]}>
										<BaseButton
											id="download-button"
											disabled={disableButtons}
											styles={[
												changeAnythingModalStyles.buttonStyles,
												changeAnythingModalStyles.downloadButton,
											]}
											startIcon={disableButtons ? <DownloadLoading /> : <DownloadDark />}
											onClick={() => {
												downloadImage(selectedPrompt?.image || '');
											}}
										>
											Download
										</BaseButton>

										<BaseButton
											id="delete-button"
											disabled={disableButtons}
											styles={[
												changeAnythingModalStyles.buttonStyles,
												changeAnythingModalStyles.deleteButton,
											]}
											onClick={() => {
												dispatch(
													toggleMultiDeletePromptConfirmationModal({
														open: true,
														prompt: [
															{
																generationId: selectedPrompt?.generationId,
																batchId: selectedPrompt?.batchId,
															},
														],
													}),
												);
											}}
										>
											<Delete />
										</BaseButton>
									</Box>
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
}

export default ChangeAnythingFeedModal;
